import { HVPT_PAIRS } from '../data/hvpt-pairs.ts';
import type { HvptContrast, HvptState, MinimalPair } from '../data/hvpt-types.ts';
import { CEFR_ORDER } from '../data/candos.ts';
import {
  loadSettings, isoDate, logSession,
  getItemState, putItemState, getAllItemStates,
} from '../data/stores.ts';
import { speak, cancelSpeech, speechCapabilities } from '../data/speech.ts';

const CONTRAST_LABEL: Record<HvptContrast, string> = {
  'y-vs-u':         '/y/ vs /ʉ/ — y norvégien vs u norvégien',
  'kj-vs-sj':       '/ç/ vs /ʃ/ — kj vs sj',
  'long-vs-short':  'Voyelle longue vs brève',
  'tone-1-vs-2':    'Tonèmes 1 et 2 (mélodie)',
  'oe-vs-aa':       '/øː/ vs /oː/ — ø vs å',
  'retroflex':      'Consonnes rétroflexes (rd, rt, rn, rs)',
};

function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

export function renderPrononciation(): HTMLElement {
  const root = document.createElement('section');
  root.className = 'flex flex-col items-center gap-6 max-w-2xl mx-auto';
  root.innerHTML = '<p class="text-[color:var(--color-ink-muted)] mt-12">Chargement de la session…</p>';
  initSession(root);
  return root;
}

async function initSession(root: HTMLElement): Promise<void> {
  const [settings, caps] = await Promise.all([loadSettings(), speechCapabilities()]);

  if (!caps.hasSpeech) {
    root.innerHTML = `
      <div class="p-6 rounded-xl border border-[color:var(--color-rorbu)] bg-[color:var(--color-rorbu)]/10 text-sm">
        Votre navigateur ne prend pas en charge la synthèse vocale (Web Speech API).
        La piste prononciation a besoin d’audio pour fonctionner.
      </div>`;
    return;
  }

  const levelIdx = CEFR_ORDER.indexOf(settings.currentLevel);
  const allowed = new Set(CEFR_ORDER.slice(0, levelIdx + 1));
  const itemsForLevel = HVPT_PAIRS.filter((p) => allowed.has(p.cefr as never));
  const states = await getAllItemStates<HvptState>('hvpt-state');
  const today = isoDate();

  const queue = shuffled(itemsForLevel)
    .map((p) => ({ p, s: states.get(p.id) }))
    .filter(({ s }) => !s || s.lastSeen !== today)
    .slice(0, settings.dailyHvptLimit)
    .map((x) => x.p);

  if (queue.length === 0) {
    root.innerHTML = '';
    root.append(renderDone(0, 0, caps.voiceCount));
    return;
  }

  let idx = 0;
  let correct = 0;

  function next(wasCorrect: boolean): void {
    if (wasCorrect) correct++;
    idx++;
    if (idx >= queue.length) {
      root.innerHTML = '';
      root.append(renderDone(idx, correct, caps.voiceCount));
      void logSession({ track: 'hvpt', count: queue.length, score: correct / queue.length });
      return;
    }
    show();
  }

  function show(): void {
    root.innerHTML = '';
    root.append(renderHeader(idx, queue.length, caps.voiceCount, caps.voiceNames));
    root.append(renderTrial(queue[idx]!, idx, next));
  }

  show();
}

function renderHeader(idx: number, total: number, voiceCount: number, voiceNames: string[]): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'w-full';
  const pct = Math.round((idx / total) * 100);
  const voiceLabel = voiceCount === 0
    ? '<span class="text-[color:var(--color-rorbu)]">aucune voix nb-NO native</span> — voix système par défaut'
    : voiceCount === 1
      ? `1 voix nb-NO (${voiceNames[0]}) — variabilité limitée. Edge expose plus de voix.`
      : `${voiceCount} voix nb-NO en rotation pour la variabilité HVPT${voiceCount < 5 ? ` (${voiceNames.slice(0, 3).join(', ')}${voiceNames.length > 3 ? '…' : ''})` : ''}`;
  wrap.innerHTML = `
    <div class="flex items-center justify-between text-sm text-[color:var(--color-ink-muted)] mb-2">
      <span>Paire ${idx + 1} sur ${total}</span>
      <span>${pct}%</span>
    </div>
    <div class="w-full h-1 rounded-full bg-[color:var(--color-border)] mb-2">
      <div class="h-full rounded-full bg-[color:var(--color-fjord-500)] transition-all" style="width:${pct}%"></div>
    </div>
    <p class="text-[10px] text-[color:var(--color-ink-muted)] italic">${voiceLabel}</p>
  `;
  return wrap;
}

function renderTrial(pair: MinimalPair, trialIdx: number, onNext: (correct: boolean) => void): HTMLElement {
  const card = document.createElement('div');
  card.className = 'w-full rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-6 flex flex-col gap-4';

  const target: 'a' | 'b' = Math.random() < 0.5 ? 'a' : 'b';
  const targetWord = pair[target].word;
  // Rotate voices for HVPT variability
  const voiceIndex = trialIdx;

  const heading = document.createElement('div');
  heading.innerHTML = `
    <p class="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded inline-block"
       style="background: var(--tint-mint-bg); color: var(--tint-mint-ink);">
      ${CONTRAST_LABEL[pair.contrast]} · ${pair.cefr}
    </p>
    <h2 class="text-lg font-medium mt-3">Quel mot avez-vous entendu&nbsp;?</h2>
    <p class="text-xs text-[color:var(--color-ink-muted)] mt-1">Écoutez puis cliquez sur le mot correspondant.</p>
  `;
  card.append(heading);

  const playBtn = document.createElement('button');
  playBtn.className = 'self-center w-16 h-16 rounded-full bg-[color:var(--color-fjord-600)] text-white text-2xl hover:bg-[color:var(--color-fjord-700)] transition-colors flex items-center justify-center';
  playBtn.innerHTML = '▶';
  playBtn.setAttribute('aria-label', 'Jouer le mot');
  let playing = false;
  playBtn.addEventListener('click', async () => {
    if (playing) return;
    playing = true;
    playBtn.innerHTML = '⏸';
    await speak(targetWord, { voiceIndex });
    playBtn.innerHTML = '▶';
    playing = false;
  });
  card.append(playBtn);

  // Auto-play once on mount (will succeed only after user interaction in some browsers; clicking ▶ is the fallback)
  setTimeout(() => { void speak(targetWord, { voiceIndex }); }, 200);

  const choices = document.createElement('div');
  choices.className = 'grid grid-cols-2 gap-3';
  let answered = false;
  for (const which of ['a', 'b'] as const) {
    const wordObj = pair[which];
    const btn = document.createElement('button');
    btn.className = 'p-4 rounded-xl border-2 border-[color:var(--color-border)] hover:border-[color:var(--color-fjord-500)] transition-all flex flex-col items-center gap-1';
    btn.innerHTML = `
      <span class="text-xl font-semibold">${wordObj.word}</span>
      <span class="text-xs font-mono text-[color:var(--color-ink-muted)]">/${wordObj.ipa}/</span>
      <span class="text-[11px] text-[color:var(--color-ink-muted)] italic">${wordObj.gloss}</span>
    `;
    btn.addEventListener('click', () => {
      if (answered) return;
      answered = true;
      const correct = which === target;
      void recordAttempt(pair.id, correct);
      // Decorate
      for (const b of choices.querySelectorAll('button')) (b as HTMLButtonElement).disabled = true;
      btn.classList.add(correct
        ? 'border-[color:var(--color-sage)]' : 'border-[color:var(--color-rorbu)]');
      btn.classList.add(correct
        ? 'bg-[color:var(--color-sage)]/15' : 'bg-[color:var(--color-rorbu)]/10');
      if (!correct) {
        const correctBtn = choices.querySelectorAll('button')[target === 'a' ? 0 : 1] as HTMLButtonElement | undefined;
        correctBtn?.classList.add('border-[color:var(--color-sage)]', 'bg-[color:var(--color-sage)]/15');
      }
      revealNote(card, pair, correct, voiceIndex, targetWord, onNext);
    });
    choices.append(btn);
  }
  card.append(choices);

  return card;
}

function revealNote(
  card: HTMLElement, pair: MinimalPair, correct: boolean, voiceIndex: number,
  targetWord: string, onNext: (correct: boolean) => void,
): void {
  const box = document.createElement('div');
  box.className = 'mt-2 p-4 rounded-lg ' + (correct
    ? 'bg-[color:var(--color-sage)]/10 border border-[color:var(--color-sage)]/40'
    : 'bg-[color:var(--color-rorbu)]/10 border border-[color:var(--color-rorbu)]/40');
  box.innerHTML = `
    <p class="text-sm">${correct ? '✓ Bonne oreille&nbsp;!' : '✗ Pas tout à fait.'}</p>
    <p class="text-xs text-[color:var(--color-ink-muted)] mt-2">${pair.note}</p>
  `;

  const replayRow = document.createElement('div');
  replayRow.className = 'flex items-center gap-2 mt-3';
  for (const w of [pair.a, pair.b] as const) {
    const b = document.createElement('button');
    b.className = 'flex-1 px-3 py-2 rounded-md border border-[color:var(--color-border)] text-xs hover:bg-[color:var(--color-fjord-50)] transition-colors';
    b.innerHTML = `🔊 « ${w.word} » /${w.ipa}/`;
    b.addEventListener('click', () => { void speak(w.word, { voiceIndex: voiceIndex + 1 }); });
    replayRow.append(b);
  }
  box.append(replayRow);
  card.append(box);

  const next = document.createElement('button');
  next.className = 'self-end px-5 py-2 rounded-lg bg-[color:var(--color-fjord-600)] text-white text-sm font-medium hover:bg-[color:var(--color-fjord-700)] transition-colors mt-2';
  next.textContent = 'Suivante →';
  next.addEventListener('click', () => { cancelSpeech(); onNext(correct); });
  card.append(next);
  // Auto-replay the target once at lower rate after wrong answer
  if (!correct) setTimeout(() => { void speak(targetWord, { voiceIndex, rate: 0.75 }); }, 400);
}

async function recordAttempt(id: string, correct: boolean): Promise<void> {
  const prev = await getItemState<HvptState>('hvpt-state', id);
  await putItemState('hvpt-state', id, {
    attempts: (prev?.attempts ?? 0) + 1,
    correct: (prev?.correct ?? 0) + (correct ? 1 : 0),
    lastSeen: isoDate(),
  });
}

function renderDone(answered: number, correct: number, voiceCount: number): HTMLElement {
  const el = document.createElement('div');
  el.className = 'flex flex-col items-center gap-4 mt-12';
  const pct = answered > 0 ? Math.round((correct / answered) * 100) : 0;
  el.innerHTML = `
    <div class="w-16 h-16 rounded-full bg-[color:var(--color-sage)] text-white flex items-center justify-center text-2xl">✓</div>
    <h2 class="text-2xl font-semibold">Session de prononciation terminée</h2>
    ${answered > 0
      ? `<p class="text-[color:var(--color-ink-muted)]">${correct} sur ${answered} correctes — ${pct}%</p>`
      : `<p class="text-[color:var(--color-ink-muted)]">Toutes les paires du jour ont déjà été vues. Revenez demain&nbsp;!</p>`}
    <p class="text-[11px] text-[color:var(--color-ink-muted)] italic">${voiceCount} voix nb-NO utilisées pour la variabilité (HVPT).</p>
    <a href="#/accueil" class="text-sm text-[color:var(--color-fjord-600)] hover:underline">← Retour à l'accueil</a>
  `;
  return el;
}
